import { checkKpiType, formatToMaxFourDigits } from '@flexo/general';
import { useMemo } from 'react';

interface KpiDataItem {
  name: string;
  value: any;
  unit?: string;
  unit_type?: string;
  type?: string;
}

interface UseKpiDataReturn {
  getCardValue: (key: string, options?: GetCardValueOptions) => CardValue;
  findApiValue: (key: string) => any;
  findApiUnit: (key: string) => string;
  formatValue: (value: any, kpiContext?: string) => string;
}

interface GetCardValueOptions {
  currencyFields?: string[];
  fallbackValue?: string;
  fallbackUnit?: string;
}

interface CardValue {
  value: any;
  unit: string;
}

/**
 * Custom hook to handle KPI data access patterns
 * @param kpiData - Array of KPI data objects
 * @param kpiContext - Context for formatting (e.g. 'economics', 'energy')
 * @returns Object with utility functions for accessing KPI data
 */
export function useKpiData(
  kpiData: KpiDataItem[] = [],
  kpiContext = 'energy'
): UseKpiDataReturn {
  const currencyFields = useMemo(
    () => ['saving', 'benefit', 'economic', 'revenue', 'cost', 'money'],
    []
  );

  const getCardValue = useMemo(
    () =>
      (key: string, options: GetCardValueOptions = {}): CardValue => {
        const {
          currencyFields: customCurrencyFields = currencyFields,
          fallbackValue = '-.-',
          fallbackUnit = '',
        } = options;

        const cardValue = kpiData?.find((el) => el.name === key);

        if (
          cardValue === null ||
          cardValue === undefined ||
          Number.isNaN(cardValue?.value)
        ) {
          return { value: fallbackValue, unit: fallbackUnit };
        }

        // Check both 'type' and 'unit_type' fields for currency detection
        // Also check kpiContext - if it's 'economics', always use economics formatting
        const isCurrency =
          kpiContext?.toLowerCase() === 'economics' ||
          customCurrencyFields?.includes((cardValue as any)?.type) ||
          customCurrencyFields?.includes((cardValue as any)?.unit_type);
        // FLEXO-6100: `?? null` keeps a legitimate 0 (rendered as "0.00"),
        // `|| null` was collapsing it into the unavailable state ("-.-").
        return isCurrency
          ? {
              value: formatToMaxFourDigits(cardValue?.value ?? null, true),
              unit: cardValue?.unit || cardValue?.unit_type || '',
            }
          : {
              value: formatToMaxFourDigits(cardValue?.value ?? null),
              unit: cardValue?.unit || cardValue?.unit_type || '',
            };
      },
    [kpiData, currencyFields, kpiContext]
  );

  const findApiValue = useMemo(
    () => (key: string) => {
      const item = kpiData?.find((data) => data.name === key);
      return item ? item.value : 0;
    },
    [kpiData]
  );

  const findApiUnit = useMemo(
    () => (key: string) => {
      const item = kpiData?.find((data) => data.name === key);
      return item ? item.unit || item.unit_type || '' : '';
    },
    [kpiData]
  );

  const formatValue = useMemo(
    () => (value: any, context?: string) => {
      if (!value && value !== 0) return '-.-';
      if (typeof value === 'string' && value?.includes('-.-')) return value;
      const contextToUse = context || kpiContext;
      return checkKpiType(contextToUse.toLowerCase(), value);
    },
    [kpiContext]
  );

  return {
    getCardValue,
    findApiValue,
    findApiUnit,
    formatValue,
  };
}

export default useKpiData;
